import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import AuthorNameLink from "./shared/AuthorNameLink";
import JobApplicationForm from "./JobApplicationForm";
import { getApiBaseUrl } from "../config/api";
import {
  canApplyForJobs,
  canManagePost,
  applicationStatusLabel,
  isHiringAcceptingApplications,
  isHiringDeadlinePassed,
  isHiringMarkedClosed,
} from "../utils/userRole";
import HiringForm from "./HiringForm";
import FitCoverImage from "./shared/FitCoverImage";

const API_URL = getApiBaseUrl();

/**
 * Hiring / job detail page with in-app Apply.
 */
export default function HiringDetail({ user, posts }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fetchedPost, setFetchedPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [localPost, setLocalPost] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [myApp, setMyApp] = useState(null);
  const [checkingApp, setCheckingApp] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cachedPost = useMemo(() => {
    const list = Array.isArray(posts) ? posts : [];
    return (
      list.find(
        (p) => String(p.id) === String(id) && p.post_type === "hiring"
      ) || null
    );
  }, [posts, id]);

  useEffect(() => {
    setLocalPost(cachedPost || fetchedPost);
  }, [cachedPost, fetchedPost]);

  const post = localPost;

  useEffect(() => {
    if (cachedPost || !id) return undefined;
    let cancelled = false;
    setLoadingPost(true);
    axios
      .get(`${API_URL}/get_hiring_post.php?id=${id}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data.post) {
          setFetchedPost(res.data.post);
        } else {
          setFetchedPost(null);
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedPost(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingPost(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cachedPost, id]);

  useEffect(() => {
    if (!user?.id || !id) return undefined;
    let cancelled = false;
    setCheckingApp(true);
    axios
      .get(
        `${API_URL}/get_application_status.php?post_id=${id}&applicant_id=${user.id}`
      )
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data.has_applied) {
          setMyApp(res.data.application);
        } else {
          setMyApp(null);
        }
      })
      .catch(() => {
        if (!cancelled) setMyApp(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingApp(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, id]);

  const toggleClosed = async () => {
    if (!user?.id || !post?.id || statusBusy) return;
    const nextClosed = !isHiringMarkedClosed(post);
    setStatusBusy(true);
    setStatusError("");
    try {
      const res = await axios.post(`${API_URL}/update_hiring_status.php`, {
        post_id: post.id,
        user_id: user.id,
        closed: nextClosed ? 1 : 0,
      });
      if (!res.data?.success || !res.data.post) {
        setStatusError(res.data?.message || "Could not update hiring status.");
        return;
      }
      setLocalPost(res.data.post);
      setFetchedPost(res.data.post);
    } catch (err) {
      setStatusError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not update hiring status."
      );
    } finally {
      setStatusBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.id || !post?.id || deleting) return;
    if (
      !window.confirm(
        "Delete this job posting? All applications and resumes for this job will also be permanently deleted."
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      const formData = new FormData();
      formData.append("post_id", post.id);
      formData.append("user_id", user.id);
      const res = await fetch(`${API_URL}/delete_post.php`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Failed to delete this post.");
        return;
      }
      navigate("/home");
    } catch (err) {
      alert(err?.message || "Failed to delete this post.");
    } finally {
      setDeleting(false);
    }
  };

  if (loadingPost && !post) {
    return (
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Loading job posting…</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Hiring post not found.</p>
        <button type="button" onClick={() => navigate("/home")}>
          Back to home
        </button>
      </div>
    );
  }

  const html = sanitizeHtml(post.hiring_description || post.content || post.text || "");
  const created = post.created_at
    ? new Date(post.created_at).toLocaleString()
    : "";
  const externalApplyUrl = post.hiring_apply_url || "";
  const isOwner = user && String(post.user_id) === String(user.id);
  const canManage = canManagePost(user, post);
  const applicantEligible = canApplyForJobs(user);
  const alreadyApplied = !!myApp;
  const markedClosed = isHiringMarkedClosed(post);
  const deadlinePassed = isHiringDeadlinePassed(post.hiring_deadline);
  const accepting = isHiringAcceptingApplications(post);
  const closedReason = markedClosed
    ? "This job posting has been closed by the employer."
    : deadlinePassed
      ? "The application deadline for this job has passed."
      : "";

  const btnBase = {
    display: "inline-block",
    marginBottom: 12,
    marginRight: 10,
    padding: "10px 18px",
    borderRadius: 10,
    fontWeight: 650,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
  };

  return (
    <div
      style={{
        maxWidth: 780,
        margin: "0 auto",
        padding: "24px 16px 48px",
        background: "#fff",
        minHeight: "100vh",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#0f766e",
          cursor: "pointer",
          fontSize: 14,
          marginBottom: 16,
          padding: 0,
        }}
      >
        ← Back
      </button>

      <FitCoverImage src={post.cover_image} maxHeight={480} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <div
          style={{
            display: "inline-block",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "#0f766e",
            background: "#ccfbf1",
            padding: "3px 8px",
            borderRadius: 4,
          }}
        >
          Hiring
        </div>
        {!accepting && (
          <div
            style={{
              display: "inline-block",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              color: "#991b1b",
              background: "#fee2e2",
              padding: "3px 8px",
              borderRadius: 4,
            }}
          >
            Closed
          </div>
        )}
      </div>

      <h1
        style={{
          fontSize: 32,
          fontWeight: 800,
          color: "#0f172a",
          margin: "0 0 12px",
          lineHeight: 1.25,
        }}
      >
        {post.title}
      </h1>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 16px",
          marginBottom: 16,
          fontSize: 14,
          color: "#475569",
        }}
      >
        {post.hiring_company && <span>{post.hiring_company}</span>}
        {post.hiring_location && <span>· {post.hiring_location}</span>}
        {post.hiring_deadline ? (
          <span style={deadlinePassed ? { color: "#b91c1c" } : undefined}>
            · {deadlinePassed ? "Deadline was" : "Apply by"} {post.hiring_deadline}
          </span>
        ) : (
          !markedClosed && <span>· Open until closed by employer</span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <img
          src={resolveAvatarUrl(post)}
          onError={handleAvatarError}
          alt=""
          style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
        />
        <div>
          <div style={{ fontWeight: 600 }}>
            <AuthorNameLink user={post} style={{ fontWeight: 600 }} />
          </div>
          {created && (
            <div style={{ fontSize: 12, color: "#64748b" }}>{created}</div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        {canManage ? (
          <>
            {isOwner ? (
              <Link
                to={`/hiring/applications?post_id=${post.id}`}
                style={{
                  ...btnBase,
                  background: "#0f766e",
                  color: "#fff",
                }}
              >
                View applications
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              style={{
                ...btnBase,
                background: "#fff",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
              }}
            >
              Edit posting
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              style={{
                ...btnBase,
                background: "#fff",
                color: "#b91c1c",
                border: "1px solid #fecaca",
                cursor: deleting ? "default" : "pointer",
                opacity: deleting ? 0.7 : 1,
              }}
            >
              {deleting ? "Deleting…" : "Delete posting"}
            </button>
            <button
              type="button"
              onClick={toggleClosed}
              disabled={statusBusy}
              style={{
                ...btnBase,
                background: markedClosed ? "#0f766e" : "#fff",
                color: markedClosed ? "#fff" : "#b91c1c",
                border: markedClosed ? "none" : "1px solid #fca5a5",
                cursor: statusBusy ? "default" : "pointer",
                opacity: statusBusy ? 0.7 : 1,
              }}
            >
              {statusBusy
                ? "Updating…"
                : markedClosed
                  ? "Reopen hiring"
                  : "Mark as closed"}
            </button>
            {statusError && (
              <p style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>
                {statusError}
              </p>
            )}
          </>
        ) : null}

        {!isOwner &&
          applicantEligible &&
          !checkingApp &&
          !alreadyApplied &&
          accepting && (
          <button
            type="button"
            onClick={() => setShowApply(true)}
            style={{
              ...btnBase,
              background: "#0f766e",
              color: "#fff",
            }}
          >
            Apply Now
          </button>
        )}

        {!isOwner &&
          applicantEligible &&
          !checkingApp &&
          !alreadyApplied &&
          !accepting && (
          <p style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>
            {closedReason}
          </p>
        )}

        {!isOwner && alreadyApplied && (
          <Link
            to="/my-applications"
            style={{
              ...btnBase,
              background: "#ccfbf1",
              color: "#0f766e",
            }}
          >
            Applied · {applicationStatusLabel(myApp?.status)} — track status
          </Link>
        )}

        {!isOwner && user && !applicantEligible && (
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
            Only User and Intern accounts can apply on-platform.
          </p>
        )}

        {externalApplyUrl && accepting && (
          <a
            href={externalApplyUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...btnBase,
              background: "#fff",
              color: "#0f766e",
              border: "1px solid #0f766e",
            }}
          >
            External apply link
          </a>
        )}
      </div>

      <div
        className="hiring-body"
        style={{ fontSize: 16, lineHeight: 1.7, color: "#334155" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <JobApplicationForm
        user={user}
        post={post}
        open={showApply}
        onClose={() => setShowApply(false)}
        onSubmitted={(app) => {
          setMyApp(app);
        }}
      />
      {showEdit ? (
        <HiringForm
          post={post}
          user={user}
          onClose={() => setShowEdit(false)}
          onSuccess={(updated) => {
            setShowEdit(false);
            if (updated) {
              setLocalPost(updated);
              setFetchedPost(updated);
            }
          }}
        />
      ) : null}
    </div>
  );
}
